import { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Typography,
  Button,
  Divider,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CustomTextInput from '../../components/Forms/CustomTextInput';
import CustomSwitch from '../../components/Forms/CustomSwitch';
import InventoryBarcodes from '../../components/MenuNew/FormView/Inventory/InventoryBarcodes';
import { UOM_OPTIONS } from '../../components/MenuNew/FormView/Inventory/configs';
import { priceTransform } from '../../helpers/formPrice';
import {
  createProduct,
  updateProduct,
  getCategoriesByMenu,
  getProductById,
  addInventoryStock,
  removeInventoryStock,
  adjustInventoryStock,
  getMenuProductsByBarcodes,
  getInventoryProduct,
  updateInventoryProduct,
} from '../../redux/actions/inventory/inventory';
import { getMenusV2 } from '../../redux/actions/Menu/menuV2';
import {
  selectCategoriesByMenu,
  selectCurrentBusiness,
} from '../../redux/selectors';
import ImageUpload from '../../components/FormFields/ImageUpload';
import StockActionModal from '../../components/Inventory/StockActionModal';
import DuplicateBarcodeModal from '../../components/Inventory/DuplicateBarcodeModal';
import { confirm } from '../../components/MenuNew/ConfirmModal';
import PromotionTab from '../PosSettings/Promotions/PromotionTab';
import usePromotionsEnabled from 'hooks/usePromotionsEnabled';

// Same table look as the line items table on Bulk Import: the main values on
// one line, every other field under it.
const CARD_SX = {
  backgroundColor: 'white',
  borderRadius: 2,
  boxShadow: 1,
  p: 3,
};
const TABLE_WRAPPER_SX = {
  border: '1px solid #e8e8e8',
  borderRadius: 1,
  overflow: 'hidden',
};
const TABLE_SX = { tableLayout: 'fixed', width: '100%' };
// Every value column has the same width; Name takes the rest of the line.
const VALUE_COLUMN_WIDTH = 140;
const HEAD_CELL_SX = {
  backgroundColor: '#f3f4f5',
  color: '#4f5969',
  fontSize: 12,
  fontWeight: 500,
  whiteSpace: 'nowrap',
};
const MAIN_ROW_SX = {
  '& > td': {
    borderBottom: '1px solid #e8e8e8',
    verticalAlign: 'middle',
    py: 1.5,
  },
};
const DETAILS_CELL_SX = { backgroundColor: '#f3f4f5', p: 2 };
// Three equal columns. A plain CSS grid, not MUI Grid: Grid's negative
// margins overflow the table cell and add scrollbars.
const DETAILS_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 2,
  alignItems: 'center',
};
const SPAN_TWO_SX = { gridColumn: 'span 2' };
const SPAN_ALL_SX = { gridColumn: '1 / -1' };
// Same height and border as the text fields next to it.
const SWITCH_BOX_SX = {
  height: 40,
  display: 'flex',
  alignItems: 'center',
  px: 1.5,
  border: '1px solid rgba(0, 0, 0, 0.23)',
  borderRadius: 1,
  backgroundColor: 'white',
};
const FULL_WIDTH_SX = { width: '100%' };
const RIGHT_ALIGNED_INPUT_SX = {
  width: '100%',
  '& input': { textAlign: 'right' },
};
const ACTION_BUTTON_SX = { minWidth: 110, height: 40 };

const InventoryItemForm = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { id, inventoryUuid } = useParams();
  const dispatch = useDispatch();
  // Inventory product without a menu item: only inventory fields are editable.
  const isInventoryEdit = Boolean(inventoryUuid);
  // New menu item linked to this existing inventory product.
  const linkedInventoryUuid =
    !id && !isInventoryEdit
      ? new URLSearchParams(location.search).get('inventoryUuid')
      : null;
  const isLinkedCreate = Boolean(linkedInventoryUuid);
  const categoriesByMenu = useSelector(selectCategoriesByMenu);
  const currentBusiness = useSelector(selectCurrentBusiness);

  const [loading, setLoading] = useState(false);
  const [stockActionLoading, setStockActionLoading] = useState(false);
  const [model, setModel] = useState({});
  const [quantity, setQuantity] = useState(0);
  const [currentItem, setCurrentItem] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null); // 'remove', 'add', or 'adjust'
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [keepLogoOriginalSize, setKeepLogoOriginalSize] = useState(false);
  const [duplicateBarcodeModalOpen, setDuplicateBarcodeModalOpen] =
    useState(false);
  const [duplicateItems, setDuplicateItems] = useState([]);
  const [pendingSaveData, setPendingSaveData] = useState(null);
  const [originalBarcodes, setOriginalBarcodes] = useState([]);
  const isEdit = Boolean(id) || isInventoryEdit;
  const promotionsEnabled = usePromotionsEnabled();

  // Prepare categories for dropdown.
  const categoryOptions = Array.isArray(categoriesByMenu)
    ? categoriesByMenu.map((cat) => ({
        value: cat._id || cat.id,
        label: cat.name,
      }))
    : [];
  const menuCategoryIds = categoryOptions.map((option) => option.value);

  // Create validation schema conditionally based on edit mode
  const validationSchema = yup.object({
    name: yup.string().required('Please enter a product name'),
    // Required for new items. Legacy items that were saved without a category
    // stay editable, so it is only enforced on create. An inventory item must
    // be saved with one of the menu's current categories: one deleted from
    // the menu shows as empty and has to be replaced.
    category: isInventoryEdit
      ? yup
          .string()
          .required('Please select a category')
          .oneOf(menuCategoryIds, 'Please select a category')
      : isEdit
      ? yup.string().nullable()
      : yup.string().required('Please select a category'),
    price: isInventoryEdit
      ? yup.mixed().nullable()
      : yup
          .mixed()
          .required('Please enter a price')
          .test(
            'is-number',
            'Please enter a valid price (must be 0 or greater)',
            (value) => {
              if (!value || value === '') return false;
              const numValue =
                typeof value === 'string' ? parseFloat(value) : value;
              return !isNaN(numValue) && numValue >= 0;
            }
          ),
    cost: yup
      .mixed()
      .nullable()
      .test(
        'is-number',
        'Please enter a valid cost (must be 0 or greater)',
        (value) => {
          if (value === '' || value === null || value === undefined)
            return true;
          const numValue =
            typeof value === 'string' ? parseFloat(value) : value;
          return !isNaN(numValue) && numValue >= 0;
        }
      ),
    quantity: isEdit
      ? yup.mixed().nullable()
      : yup
          .mixed()
          .nullable()
          .transform((value, originalValue) => {
            // Transform empty string to null to prevent NaN errors
            if (
              originalValue === '' ||
              originalValue === null ||
              originalValue === undefined
            ) {
              return null;
            }
            // Convert string to number if possible
            const numValue =
              typeof originalValue === 'string'
                ? parseFloat(originalValue)
                : originalValue;
            return isNaN(numValue) ? null : numValue;
          })
          .test(
            'is-number',
            'Please enter a valid quantity (must be 0 or greater)',
            (value) => {
              // Allow null/undefined (will default to 0)
              if (value === null || value === undefined) return true;
              return typeof value === 'number' && value >= 0;
            }
          ),
    sku: yup.string().nullable(),
    unit: isLinkedCreate
      ? yup.string().nullable()
      : yup.string().required('Please select a unit of measure'),
    // Barcodes are optional. When none are provided the backend keeps its
    // existing behavior of generating one from the SKU.
    barcodes: yup.array().of(yup.string()).nullable(),
    trackInventory: yup.boolean(),
  });

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      category: '',
      price: '',
      cost: '',
      quantity: '',
      sku: '',
      unit: 'each',
      barcodes: [],
      image: null,
      trackInventory: true,
    },
  });

  const { setValue, trigger, formState } = methods;
  const isDirty = formState.isDirty;

  const onChangePrice = async (e) => {
    const { name, value } = e.target;
    const transformedValue = priceTransform(value);
    setValue(name, transformedValue, { shouldDirty: true });

    // Trigger validation to update error state
    await trigger(name);
  };

  const onChangeCost = async (e) => {
    const { name, value } = e.target;
    const transformedValue = priceTransform(value);
    setValue(name, transformedValue, { shouldDirty: true });

    // Trigger validation to update error state
    await trigger(name);
  };

  // Handle browser back/close with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue =
          'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  // Fetch categories and products on mount
  useEffect(() => {
    dispatch(getCategoriesByMenu());
    // No need to get products here since inventory list is already available
  }, [dispatch]);

  // Reset keepLogoOriginalSize when productId changes
  useEffect(() => {
    setKeepLogoOriginalSize(false);
  }, [id]);

  // Load the inventory product (inventory edit, or new menu item linked to it)
  const inventoryProductUuid = inventoryUuid || linkedInventoryUuid;
  useEffect(() => {
    if (!inventoryProductUuid) return;
    setLoading(true);
    dispatch(getInventoryProduct(inventoryProductUuid))
      .then((inventoryProduct) => {
        // An inventory product with a menu item is edited through that item,
        // the first one in the inventory list's order.
        if (isInventoryEdit && inventoryProduct.menuProductIds.length > 0) {
          history.replace({
            pathname: `/inventory/items/${inventoryProduct.menuProductIds[0]}`,
            search: new URLSearchParams({
              name: inventoryProduct.name,
            }).toString(),
          });
          return;
        }
        setCurrentItem({ inventoryInfo: inventoryProduct });
        setQuantity(inventoryProduct.stock || 0);
        const initialBarcodes = inventoryProduct.barcodeNumber || [];
        setOriginalBarcodes(initialBarcodes);
        methods.reset({
          name: inventoryProduct.name || '',
          category: isInventoryEdit ? inventoryProduct.categoryId || '' : '',
          price: '',
          cost:
            inventoryProduct.unitPrice != null
              ? String(inventoryProduct.unitPrice)
              : '',
          quantity: '',
          sku: inventoryProduct.sku || '',
          unit: inventoryProduct.unit || '',
          barcodes: initialBarcodes,
          image: null,
          trackInventory: inventoryProduct.trackInventory ?? false,
        });
      })
      .catch((error) => {
        console.error('Error loading inventory product:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [inventoryProductUuid, isInventoryEdit, history, methods, dispatch]);

  // Load item data for editing
  useEffect(() => {
    if (id) {
      setLoading(true);
      dispatch(getProductById(id))
        .then((response) => {
          const productData = response?.data?.data;
          if (productData) {
            setCurrentItem(productData);
            setQuantity(productData.inventoryInfo?.stock || 0);
            const initialBarcodes = productData.barcodeNumbers || [];
            setOriginalBarcodes(initialBarcodes);
            methods.reset({
              name: productData.name || '',
              category: productData.category?._id || productData.category || '',
              price: String(productData.price) || '0',
              cost: String(productData.inventoryInfo?.unitPrice || '0'),
              sku: productData.inventoryInfo?.sku || '',
              unit: productData.inventoryInfo?.unit || productData.unit || '',
              barcodes: initialBarcodes,
              image: productData.image || null,
              trackInventory:
                productData.inventoryInfo?.trackInventory ?? false,
            });
            setModel({ image: productData.image });
          }
        })
        .catch((error) => {
          console.error('Error loading product:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, methods, dispatch]);

  const performSave = async (formData) => {
    if (isInventoryEdit) {
      await dispatch(
        updateInventoryProduct(inventoryUuid, {
          name: formData.name,
          unitPrice: formData.unitPrice,
          sku: formData.sku,
          unit: formData.unit,
          barcodeNumber: formData.barcodeNumbers,
          trackInventory: formData.trackInventory,
          ...(formData.categoryId && {
            categoryId: formData.categoryId,
            categoryName: categoryOptions.find(
              (option) => option.value === formData.categoryId
            ).label,
          }),
        })
      );
    } else if (isEdit) {
      await dispatch(updateProduct(id, formData));
    } else {
      await dispatch(createProduct(formData));
    }

    // Update original barcodes after successful save
    if (formData.barcodeNumbers) {
      setOriginalBarcodes(formData.barcodeNumbers);
    }

    // Refresh menu data after successful save
    await dispatch(getMenusV2(false));

    // Reset dirty state after successful save
    methods.reset(methods.getValues());
    if (isLinkedCreate) {
      // The page we came from shows the inventory item as having no menu item.
      history.replace('/inventory/items');
    } else {
      history.goBack();
    }
  };

  const handleSave = async (data) => {
    console.log('Form data:', data);
    setLoading(true);
    try {
      if (isLinkedCreate) {
        const linkedFormData = {
          name: data.name,
          price: parseFloat(data.price || '0'),
          categoryId: data.category,
          inventoryUuid: linkedInventoryUuid,
        };
        if (data.image) {
          linkedFormData.image = data.image;
        }
        await performSave(linkedFormData);
        return;
      }

      const formData = {
        name: data.name,
        price: parseFloat(data.price || '0'),
        barcodeNumbers: data.barcodes || [],
        unit: data.unit,
        trackInventory: data.trackInventory ?? false,
      };

      // Always set on create, where the field is required. On edit a legacy
      // item may still have no category: the API rejects an empty categoryId
      // but leaves the current one untouched when it is omitted.
      if (data.category) {
        formData.categoryId = data.category;
      }

      // SKU is optional. Only send it when the user typed one; when blank the
      // backend keeps auto-generating a SKU.
      if (data.sku && data.sku.trim()) {
        formData.sku = data.sku.trim();
      }

      // Add unitPrice to inventoryInfo if cost is provided
      if (data.cost !== undefined && data.cost !== '') {
        formData.unitPrice = parseFloat(data.cost);
      }

      // Only add image if it exists
      if (data.image) {
        formData.image = data.image;
      }

      // Only add stock in add new mode
      if (!isEdit) {
        formData.stock = Number(data.quantity) || 0;
      }

      // Check for duplicate barcodes in the menu (done by the inventory service)
      // Only check newly added barcodes, not already saved ones
      const currentBarcodes = formData.barcodeNumbers || [];
      const normalizedCurrentBarcodes = currentBarcodes
        .map((b) => String(b).trim())
        .filter((b) => b);
      const normalizedOriginalBarcodes = originalBarcodes
        .map((b) => String(b).trim())
        .filter((b) => b);

      // Find newly added barcodes (in current but not in original)
      const originalBarcodesSet = new Set(normalizedOriginalBarcodes);
      const newlyAddedBarcodes = normalizedCurrentBarcodes.filter(
        (barcode) => !originalBarcodesSet.has(barcode)
      );

      // Only check for duplicates if there are newly added barcodes
      // For new items (not edit), check all barcodes
      const barcodesToCheck = isEdit
        ? newlyAddedBarcodes
        : normalizedCurrentBarcodes;

      if (barcodesToCheck.length > 0) {
        const duplicateProducts = await dispatch(
          getMenuProductsByBarcodes({
            barcodes: barcodesToCheck,
            excludeProductId: isEdit ? id : null,
          })
        );
        const checkedBarcodes = new Set(barcodesToCheck);
        const duplicateItemsData = duplicateProducts.map((product) => ({
          _id: product._id,
          name: product.name || 'Unnamed Item',
          price: product.price || 0,
          image: product.image || null,
          duplicateBarcodes: product.barcodeNumbers
            .map((barcode) => String(barcode).trim())
            .filter((barcode) => checkedBarcodes.has(barcode)),
        }));

        if (duplicateItemsData.length > 0) {
          // Store the form data and show duplicate modal
          setPendingSaveData(formData);
          setDuplicateItems(duplicateItemsData);
          setDuplicateBarcodeModalOpen(true);
          setLoading(false);
          return;
        }
      }

      // No duplicates found, proceed with save
      await performSave(formData);
    } catch (error) {
      console.error('Error saving item:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueWithDuplicate = async () => {
    setDuplicateBarcodeModalOpen(false);
    if (pendingSaveData) {
      setLoading(true);
      try {
        await performSave(pendingSaveData);
      } catch (error) {
        console.error('Error saving item:', error);
      } finally {
        setLoading(false);
        setPendingSaveData(null);
        setDuplicateItems([]);
      }
    }
  };

  const handleCancelDuplicate = () => {
    setDuplicateBarcodeModalOpen(false);
    setPendingSaveData(null);
    setDuplicateItems([]);
  };

  const handleCancel = async () => {
    if (isDirty) {
      const result = await confirm({
        title: 'confirm_modal.title',
        message: 'You have unsaved changes. Are you sure you want to leave?',
        cancelText: 'confirm_modal.cancel',
        confirmText: 'confirm_modal.save_and_leave',
        discardText: 'confirm_modal.discard',
        hideCloseIcon: false,
        hideDiscardBtn: false,
        background: '#1976d2',
      });
      if (result === true) {
        // Save and leave - save first then go back
        const formData = methods.getValues();
        await handleSave(formData);
      } else if (result === 'discard') {
        // Discard changes and go back
        history.goBack();
      }
      // If result is false, user cancelled - do nothing
    } else {
      history.goBack();
    }
  };

  const handleActionClick = (action) => {
    setSelectedAction(action);
    setStockModalOpen(true);
  };

  const handleCloseStockModal = () => {
    setStockModalOpen(false);
    setSelectedAction(null);
  };

  const handleStockAction = async (stockQuantity) => {
    if (!isEdit || !currentItem || !selectedAction) return;

    try {
      setStockActionLoading(true);
      const inventory = currentItem?.inventoryInfo || {};

      const requestData = {
        uuid: inventory.uuid || currentItem.uuid || '',
        stock: Number(stockQuantity) || 0,
        businessUuid: currentBusiness?.uuid || '',
      };

      let apiCall;
      switch (selectedAction) {
        case 'add':
          apiCall = addInventoryStock(requestData);
          break;
        case 'remove':
          apiCall = removeInventoryStock(requestData);
          break;
        case 'adjust':
          apiCall = adjustInventoryStock(requestData);
          break;
        default:
          throw new Error('Invalid action');
      }

      await dispatch(apiCall);
      if (isInventoryEdit) {
        const inventoryProduct = await dispatch(
          getInventoryProduct(inventoryUuid)
        );
        setQuantity(inventoryProduct.stock);
      } else {
        const response = await dispatch(getProductById(id));
        const productData = response?.data?.data;
        if (productData?.inventoryInfo?.stock !== undefined) {
          setQuantity(productData.inventoryInfo.stock);
        }
      }
      handleCloseStockModal();
    } catch (error) {
      console.error('Error updating stock:', error);
    } finally {
      setStockActionLoading(false);
    }
  };

  // Prepare UOM options for dropdown
  const unitOptions = UOM_OPTIONS.map((unit) => ({
    value: unit.value,
    label: unit.label,
  }));

  const columns = [
    { key: 'name', label: `${t('Name')} *` },
    {
      key: 'quantity',
      label: t('Quantity'),
      align: 'right',
      width: VALUE_COLUMN_WIDTH,
    },
    {
      key: 'cost',
      label: t('Avg Cost'),
      align: 'right',
      width: VALUE_COLUMN_WIDTH,
    },
    ...(isEdit
      ? [
          {
            key: 'lastInvoiceCost',
            label: t('Last Invoice Cost'),
            align: 'right',
            width: VALUE_COLUMN_WIDTH,
          },
        ]
      : []),
    ...(!isInventoryEdit
      ? [
          {
            key: 'price',
            label: `${t('Price')} *`,
            align: 'right',
            width: VALUE_COLUMN_WIDTH,
          },
        ]
      : []),
  ];

  return (
    <FormProvider {...methods}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Box
          component="form"
          onSubmit={methods.handleSubmit(handleSave, (errors) => {
            console.log('Form validation errors:', errors);
            // You can add toast/alert here to show errors to user
          })}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Box sx={CARD_SX}>
            {/* Title */}
            <Typography
              sx={{
                mb: 2,
                fontWeight: '400',
                fontSize: '22px',
              }}
            >
              {isInventoryEdit
                ? t('Edit Inventory Item')
                : isLinkedCreate
                ? t('Create Menu Item')
                : isEdit
                ? t('Edit Item')
                : t('Add New')}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {isInventoryEdit && (
              <Alert
                severity="warning"
                sx={{ mb: 3, width: '606px' }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    disabled={loading}
                    onClick={() =>
                      history.push(
                        `/inventory/items/new?inventoryUuid=${encodeURIComponent(
                          inventoryUuid
                        )}`
                      )
                    }
                  >
                    {t('Create Menu Item')}
                  </Button>
                }
              >
                {t('This inventory item has no menu item.')}
              </Alert>
            )}
            {isLinkedCreate && (
              <Alert severity="info" sx={{ mb: 3, width: '606px' }}>
                {t(
                  'The menu item is linked to this inventory item. Inventory fields are edited on the inventory item.'
                )}
              </Alert>
            )}

            <Box sx={TABLE_WRAPPER_SX}>
              <Table size="small" sx={TABLE_SX}>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        align={column.align}
                        sx={{ ...HEAD_CELL_SX, width: column.width }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={MAIN_ROW_SX}>
                    <TableCell>
                      <CustomTextInput
                        sx={FULL_WIDTH_SX}
                        name="name"
                        inputProps={{ 'aria-label': t('Name') }}
                        disabled={loading}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {isEdit || isLinkedCreate ? (
                        <TextField
                          sx={RIGHT_ALIGNED_INPUT_SX}
                          type="number"
                          value={quantity}
                          inputProps={{
                            min: 0,
                            readOnly: true,
                            'aria-label': t('Quantity'),
                          }}
                          focused={false}
                          disabled={true}
                          size="small"
                        />
                      ) : (
                        <CustomTextInput
                          sx={RIGHT_ALIGNED_INPUT_SX}
                          name="quantity"
                          placeholder="0"
                          inputProps={{
                            type: 'number',
                            min: 0,
                            'aria-label': t('Quantity'),
                          }}
                          disabled={loading}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <CustomTextInput
                        sx={RIGHT_ALIGNED_INPUT_SX}
                        name="cost"
                        inputProps={{
                          type: 'number',
                          step: 'any',
                          'aria-label': t('Avg Cost'),
                        }}
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ mr: 1 }}>$</Typography>
                          ),
                        }}
                        onChange={onChangeCost}
                        disabled={
                          loading || stockActionLoading || isLinkedCreate
                        }
                      />
                    </TableCell>
                    {isEdit && (
                      <TableCell align="right">
                        <TextField
                          sx={RIGHT_ALIGNED_INPUT_SX}
                          size="small"
                          variant="outlined"
                          value={
                            currentItem?.inventoryInfo?.metadata
                              ?.lastInvoiceUnitPrice != null
                              ? String(
                                  currentItem.inventoryInfo.metadata
                                    .lastInvoiceUnitPrice
                                )
                              : ''
                          }
                          inputProps={{ 'aria-label': t('Last Invoice Cost') }}
                          InputProps={{
                            readOnly: true,
                            startAdornment: (
                              <Typography sx={{ mr: 1 }}>$</Typography>
                            ),
                          }}
                          disabled
                        />
                      </TableCell>
                    )}
                    {!isInventoryEdit && (
                      <TableCell align="right">
                        <CustomTextInput
                          sx={RIGHT_ALIGNED_INPUT_SX}
                          name="price"
                          inputProps={{
                            type: 'number',
                            step: 'any',
                            'aria-label': t('Price'),
                          }}
                          InputProps={{
                            startAdornment: (
                              <Typography sx={{ mr: 1 }}>$</Typography>
                            ),
                          }}
                          onChange={onChangePrice}
                          disabled={loading || stockActionLoading}
                        />
                      </TableCell>
                    )}
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={columns.length} sx={DETAILS_CELL_SX}>
                      <Box sx={DETAILS_GRID_SX}>
                        <Box>
                          <CustomTextInput
                            sx={FULL_WIDTH_SX}
                            select
                            name="category"
                            label={`${t('Category')} *`}
                            disabled={loading}
                          >
                            {categoryOptions.map((option) => (
                              <MenuItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                          </CustomTextInput>
                        </Box>
                        <Box>
                          <CustomTextInput
                            sx={FULL_WIDTH_SX}
                            name="sku"
                            label={t('SKU')}
                            placeholder={t('Auto-generated if left blank')}
                            disabled={loading || isLinkedCreate}
                          />
                        </Box>
                        <Box>
                          <CustomTextInput
                            sx={FULL_WIDTH_SX}
                            select
                            name="unit"
                            label={`${t('Unit of Measure')} *`}
                            disabled={loading || isLinkedCreate}
                          >
                            {unitOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </CustomTextInput>
                        </Box>
                        <Box sx={SPAN_TWO_SX}>
                          <InventoryBarcodes
                            name="barcodes"
                            label={t('Barcodes')}
                            control={methods.control}
                            readOnly={isLinkedCreate}
                            isDisabled={loading || isLinkedCreate}
                          />
                        </Box>
                        <Box sx={SWITCH_BOX_SX}>
                          <CustomSwitch
                            name="trackInventory"
                            label={t('Track Inventory')}
                            disabled={loading || isLinkedCreate}
                          />
                        </Box>
                        {!isInventoryEdit && (
                          <Box sx={SPAN_ALL_SX}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                flexWrap: 'wrap',
                              }}
                            >
                              <ImageUpload
                                setFormValue={methods.setValue}
                                initialValue={model?.image}
                                name="image"
                                model={model}
                                setModel={setModel}
                                className="d-block"
                                size={72}
                                removable
                                keepLogoOriginalSize={keepLogoOriginalSize}
                              />
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 'bold' }}
                                >
                                  {t('Item Photo')}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: '13px', color: '#929398' }}
                                >
                                  JPG, JPEG or PNG.
                                </Typography>
                                <FormGroup>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={keepLogoOriginalSize}
                                        onChange={(event) =>
                                          setKeepLogoOriginalSize(
                                            event.target.checked
                                          )
                                        }
                                      />
                                    }
                                    label="Keep original size"
                                  />
                                </FormGroup>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Box>

          {/* Stock actions (edit only) and Cancel / Save */}
          <Box
            sx={{
              ...CARD_SX,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            {isEdit && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => handleActionClick('remove')}
                  sx={ACTION_BUTTON_SX}
                  disabled={loading || stockActionLoading}
                >
                  {t('REMOVE')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleActionClick('add')}
                  sx={ACTION_BUTTON_SX}
                  disabled={loading || stockActionLoading}
                >
                  {t('ADD')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleActionClick('adjust')}
                  sx={ACTION_BUTTON_SX}
                  disabled={loading || stockActionLoading}
                >
                  {t('ADJUST')}
                </Button>
              </Box>
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
              sx={ACTION_BUTTON_SX}
            >
              {t('CANCEL')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={loading || !isDirty}
              sx={ACTION_BUTTON_SX}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {t('SAVE')}
            </Button>
          </Box>
        </Box>

        {Boolean(id) && promotionsEnabled && (
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 2,
              boxShadow: 1,
              p: 2,
            }}
          >
            <Typography sx={{ fontWeight: '400', fontSize: '18px', mb: 2 }}>
              {t('Promotions')}
            </Typography>
            <PromotionTab
              lockedProductIds={[currentItem?.uuid]}
              lockedProductName={currentItem?.name || methods.getValues().name}
            />
          </Box>
        )}
      </Box>

      {/* Stock Action Modal */}
      <StockActionModal
        open={stockModalOpen}
        onClose={handleCloseStockModal}
        action={selectedAction}
        onSave={handleStockAction}
        loading={stockActionLoading}
      />

      {/* Duplicate Barcode Modal */}
      <DuplicateBarcodeModal
        open={duplicateBarcodeModalOpen}
        onClose={handleCancelDuplicate}
        onContinue={handleContinueWithDuplicate}
        duplicateItems={duplicateItems}
      />
    </FormProvider>
  );
};

export default InventoryItemForm;
